import { createFileRoute } from "@tanstack/react-router";
import { PortalLayout } from "@/components/portal-layout";
import { useAuth } from "@/lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Check } from "lucide-react";
import { toast } from "sonner";
import * as React from "react";

export const Route = createFileRoute("/admin/my-attendance")({
  component: MyAttendancePage,
});

function MyAttendancePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedClass, setSelectedClass] = React.useState<string>("");
  const [selectedSession, setSelectedSession] = React.useState<string>("");

  const classesQ = useQuery({
    queryKey: ["admin-teacher-classes", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("classes")
        .select("id, name, subject")
        .eq("teacher_id", user!.id)
        .eq("status", "active");
      return data ?? [];
    },
  });

  const sessionsQ = useQuery({
    queryKey: ["admin-teacher-sessions", selectedClass],
    enabled: !!selectedClass,
    queryFn: async () => {
      const { data } = await supabase
        .from("class_sessions")
        .select("id, starts_at, ends_at, topic")
        .eq("class_id", selectedClass)
        .order("starts_at", { ascending: false })
        .limit(20);
      return data ?? [];
    },
  });

  const attendanceQ = useQuery({
    queryKey: ["admin-teacher-attendance", selectedSession],
    enabled: !!selectedSession && !!selectedClass,
    queryFn: async () => {
      const { data: enrollments } = await supabase
        .from("enrollments")
        .select("student_id, profiles!inner(id, display_name, email)")
        .eq("class_id", selectedClass)
        .eq("is_active", true);

      const { data: existing } = await supabase
        .from("attendances")
        .select("student_id, status")
        .eq("session_id", selectedSession);

      const attMap = new Map((existing ?? []).map((a) => [a.student_id, a.status]));

      return (enrollments ?? []).map((e) => {
        const prof = e.profiles as { display_name?: string | null; email?: string | null } | null;
        return {
          studentId: e.student_id,
          name: prof?.display_name ?? prof?.email?.split("@")[0] ?? "—",
          status: attMap.get(e.student_id) ?? "absent",
        };
      });
    },
  });

  const saveMut = useMutation({
    mutationFn: async (records: { studentId: string; status: string }[]) => {
      for (const r of records) {
        await supabase
          .from("attendances")
          .upsert(
            { session_id: selectedSession, student_id: r.studentId, status: r.status },
            { onConflict: "session_id,student_id" },
          );
      }
    },
    onSuccess: () => {
      toast.success("Đã lưu điểm danh!");
      queryClient.invalidateQueries({ queryKey: ["admin-teacher-attendance"] });
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Lưu điểm danh thất bại");
    },
  });

  const [localAtt, setLocalAtt] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (attendanceQ.data) {
      setLocalAtt(Object.fromEntries(attendanceQ.data.map((a) => [a.studentId, a.status])));
    }
  }, [attendanceQ.data]);

  return (
    <PortalLayout
      portal="admin"
      title="Điểm danh"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Điểm danh" }]}
    >
      <div className="flex flex-wrap gap-3 mb-6">
        <Select
          value={selectedClass}
          onValueChange={(v) => {
            setSelectedClass(v);
            setSelectedSession("");
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Chọn lớp" />
          </SelectTrigger>
          <SelectContent>
            {(classesQ.data ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedClass && (
          <Select value={selectedSession} onValueChange={setSelectedSession}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Chọn buổi" />
            </SelectTrigger>
            <SelectContent>
              {(sessionsQ.data ?? []).map((s, i) => (
                <SelectItem key={s.id} value={s.id}>
                  Buổi {(sessionsQ.data?.length ?? 0) - i} —{" "}
                  {new Date(s.starts_at).toLocaleDateString("vi-VN")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {!selectedSession ? (
        <div className="text-center py-16">
          <ClipboardCheck className="size-10 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">Chọn lớp và buổi học để điểm danh.</p>
        </div>
      ) : attendanceQ.isLoading ? (
        <div className="text-center py-10 text-sm text-muted-foreground">Đang tải...</div>
      ) : (
        <>
          <div className="bg-card border rounded-xl overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Học viên</th>
                  <th className="text-center p-3 font-medium w-[140px]">Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {(attendanceQ.data ?? []).map((a) => (
                  <tr key={a.studentId} className="border-b last:border-0">
                    <td className="p-3">{a.name}</td>
                    <td className="p-3 text-center">
                      <Select
                        value={localAtt[a.studentId] ?? "absent"}
                        onValueChange={(v) =>
                          setLocalAtt((prev) => ({ ...prev, [a.studentId]: v }))
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs mx-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="present">Có mặt</SelectItem>
                          <SelectItem value="absent">Vắng</SelectItem>
                          <SelectItem value="late">Trễ</SelectItem>
                          <SelectItem value="excused">Có phép</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            onClick={() =>
              saveMut.mutate(
                Object.entries(localAtt).map(([studentId, status]) => ({ studentId, status })),
              )
            }
            disabled={saveMut.isPending}
          >
            <Check className="size-4 mr-1" /> Lưu điểm danh
          </Button>
        </>
      )}
    </PortalLayout>
  );
}
